import { useState, useEffect } from 'react'
import { Scanner } from '@yudiel/react-qr-scanner'
import { Button } from '../common/Button'
import { Card } from '../common/Card'
import type { Challenge, ChallengeAnswer } from '../../types'

interface QRScannerProps {
  challenge: Challenge
  onSubmit: (answer: ChallengeAnswer) => void
  isSubmitting: boolean
  debugMode?: boolean
}

export function QRScanner({
  challenge,
  onSubmit,
  isSubmitting,
  debugMode,
}: QRScannerProps) {
  const [scanned, setScanned] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cameraReady, setCameraReady] = useState(false)

  useEffect(() => {
    // Check camera permissions
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then(() => setCameraReady(true))
      .catch(() => setError('Camera permission denied. Please allow camera access to scan QR codes.'))
  }, [])

  const handleScan = (result: { rawValue: string }[]) => {
    if (!scanned && result.length > 0) {
      const value = result[0].rawValue
      setScanned(true)
      onSubmit({ qr_value: value })
    }
  }

  const handleError = (err: unknown) => {
    console.error('QR Scanner error:', err)
    setError('Failed to access camera. Please try again.')
  }

  const handleManualSubmit = () => {
    if (challenge.debug_answer) {
      onSubmit({ qr_value: challenge.debug_answer })
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Card variant="parchment" padding="md">
          <p className="text-error-600">{error}</p>
        </Card>
        {debugMode && challenge.debug_answer && (
          <Button onClick={handleManualSubmit} className="w-full">
            Debug: Submit Correct Value
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <Card variant="parchment" padding="md">
        <p className="text-brown-800">
          {challenge.qr_hint || challenge.instructions || 'Scan the QR code to continue'}
        </p>
      </Card>

      {!scanned && cameraReady && (
        <div className="relative aspect-square rounded-lg overflow-hidden bg-black">
          <Scanner
            onScan={handleScan}
            onError={handleError}
            constraints={{ facingMode: 'environment' }}
            styles={{
              container: { width: '100%', height: '100%' },
              video: { width: '100%', height: '100%', objectFit: 'cover' },
            }}
          />
          {/* Scanning overlay */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-white/70 rounded-lg">
              <div className="absolute top-0 left-0 w-4 h-4 border-t-4 border-l-4 border-primary-500 rounded-tl" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-4 border-r-4 border-primary-500 rounded-tr" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-4 border-l-4 border-primary-500 rounded-bl" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-4 border-r-4 border-primary-500 rounded-br" />
            </div>
          </div>
        </div>
      )}

      {!cameraReady && !error && (
        <div className="aspect-square rounded-lg bg-cream-100 flex items-center justify-center">
          <div className="text-center text-brown-500">
            <svg
              className="w-12 h-12 mx-auto mb-2 animate-pulse"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <p>Starting camera...</p>
          </div>
        </div>
      )}

      {scanned && (
        <Card padding="md" className="text-center">
          <svg
            className="w-12 h-12 mx-auto text-success-500 mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-brown-800 font-medium">QR Code Scanned!</p>
          {isSubmitting && <p className="text-brown-500 text-sm">Verifying...</p>}
        </Card>
      )}

      {debugMode && challenge.debug_answer && !scanned && (
        <Button variant="outline" onClick={handleManualSubmit} className="w-full">
          Debug: Submit Correct Value
        </Button>
      )}
    </div>
  )
}
